import {
  getMaxBenefitBenefitPerPeriodFields,
  deathBenefitAtAge,
  policyBenefitPeriodMonths,
  policyMaximumBenefitAmount,
  policyTotalLTCBalance,
  isPrimaryPolicyHolder,
  expectedPolicyActivationAge,
  monthlyBenefitWithdrawlRate,
  hasCobRider,
  deathBenefitFaceAmount,
  policyMinimumGuaranteedBenefitAmount,
  policyContinuationBenefitAmount,
  policyTotalLTCBenefitAmount,
  policyInflationProtectionPercentage,
  policyInflationProtectionType,
  commonFields,
  MaxBenefitPeriod,
  fieldOverride,
  policyLumpSumPayment,
  policyLimitedPayYears,
  policyPremiumMonthlyCost,
  homeCareWaitingPeriodDays,
  policyPremiumIncreaseRate,
  policyPremiumIncreaseYears,
  facilityCareWaitingPeriodDays,
  policyPremiumStartYear,
  PolicyPromptField,
  policyPaymentType,
  policyInflationProtectionOnCOBAmount,
  policyInflationProtectionOnFaceAmount,
  policyInflationProtectionYears,
} from './policyUploadFields';
import { FundingPolicyType } from '../appModel';
import { fundingPolicyTypeDefList } from '../FundingPolicyTypeDef';

const hybridLifeIraCommonFields = commonFields.filter(
  field =>
    field.fieldId !== 'policyLumpSumPayment' &&
    field.fieldId !== 'policyLimitedPayYears' &&
    field.fieldId !== 'policyPremiumMonthlyCost' &&
    field.fieldId !== 'policyPaymentType',
);

const hybridLifeIraFields = [
  fieldOverride(policyLumpSumPayment, {
    fieldDescription: `This value will depend on Has COB Rider Option. If "Has COB Rider Option" is yes, then look for the exact row label "1st Year Premium" associated to the column "Base Policy + COB Rider". This amount should be more than the 1st year premium for just the base policy. If "Has COB Rider Option" is no, then get the "1st Year Premium" associated to the column "Base Policy".
    This data should be located next to "Subsequent COB Premium" amount. Importantly, DO NOT return the "Contract Premium" amount which is found in the header of several pages.`,
  }),
  fieldOverride(policyLimitedPayYears, {
    fieldDescription: `Look for the number of payments needed for the COB rider. It will be in the format "COB X-Pay Rider"`,
  }),
  fieldOverride(policyPremiumMonthlyCost, {
    fieldDescription: `Look for the "Subsequent COB Premium" which should be annual. If so, divide this amount by 12 to get the monthly premium amount.`,
  }),
];

export const policyTypeFieldMap = {
  [FundingPolicyType.hybridLifeInsurance]: [
    deathBenefitFaceAmount,
    policyMinimumGuaranteedBenefitAmount,
    policyContinuationBenefitAmount,
    policyTotalLTCBenefitAmount,
    ...getMaxBenefitBenefitPerPeriodFields([MaxBenefitPeriod.daily, MaxBenefitPeriod.monthly]),
    ...commonFields,
    policyInflationProtectionPercentage,
    policyInflationProtectionType,
  ],
  [FundingPolicyType.longTermCareInsurance]: [
    policyMaximumBenefitAmount,
    ...getMaxBenefitBenefitPerPeriodFields([MaxBenefitPeriod.daily, MaxBenefitPeriod.monthly]),
    ...commonFields,
    policyInflationProtectionPercentage,
    policyInflationProtectionType,
    policyInflationProtectionYears,
    policyPremiumIncreaseRate,
    policyPremiumIncreaseYears,
  ],
  [FundingPolicyType.lifeInsuranceWithRider]: [
    deathBenefitFaceAmount,
    policyMinimumGuaranteedBenefitAmount,
    ...getMaxBenefitBenefitPerPeriodFields([MaxBenefitPeriod.daily, MaxBenefitPeriod.monthly]),
    ...commonFields,
    policyInflationProtectionPercentage,
    policyInflationProtectionType,
  ],
  [FundingPolicyType.shortTermCareInsurance]: [
    policyBenefitPeriodMonths,
    policyMaximumBenefitAmount,
    ...getMaxBenefitBenefitPerPeriodFields([MaxBenefitPeriod.daily, MaxBenefitPeriod.monthly]),
    ...commonFields,
    policyInflationProtectionPercentage,
    policyInflationProtectionType,
  ],
  [FundingPolicyType.annuityHybrid]: [
    policyTotalLTCBalance,
    ...getMaxBenefitBenefitPerPeriodFields([
      MaxBenefitPeriod.daily,
      MaxBenefitPeriod.monthly,
      MaxBenefitPeriod.annual,
    ]),
    ...commonFields,
  ],
  [FundingPolicyType.jointHybridAssetBased]: [
    deathBenefitFaceAmount,
    policyMinimumGuaranteedBenefitAmount,
    policyContinuationBenefitAmount,
    policyTotalLTCBenefitAmount,
    ...getMaxBenefitBenefitPerPeriodFields([MaxBenefitPeriod.daily, MaxBenefitPeriod.monthly]),
    ...commonFields,
    policyInflationProtectionPercentage,
    policyInflationProtectionType,
  ],
  [FundingPolicyType.jointHybridAnnuityBased]: [
    isPrimaryPolicyHolder,
    expectedPolicyActivationAge,
    policyTotalLTCBalance,
    ...getMaxBenefitBenefitPerPeriodFields([
      MaxBenefitPeriod.daily,
      MaxBenefitPeriod.monthly,
      MaxBenefitPeriod.annual,
    ]),
    ...commonFields,
  ],
  [FundingPolicyType.lifeWithIndexAccount]: [
    deathBenefitAtAge,
    monthlyBenefitWithdrawlRate,
    policyMinimumGuaranteedBenefitAmount,
    ...getMaxBenefitBenefitPerPeriodFields([MaxBenefitPeriod.daily, MaxBenefitPeriod.monthly]),
    ...commonFields,
  ],
  [FundingPolicyType.hybridLifeIra]: [
    hasCobRider,
    policyTotalLTCBalance,
    {
      ...policyContinuationBenefitAmount,
      fieldDescription: '',
    },
    {
      ...monthlyBenefitWithdrawlRate,
      fieldDescription: '',
    },
    ...getMaxBenefitBenefitPerPeriodFields([MaxBenefitPeriod.daily, MaxBenefitPeriod.monthly]),
    ...hybridLifeIraCommonFields,
    ...hybridLifeIraFields,
    fieldOverride(policyInflationProtectionOnFaceAmount, {
      fieldTitle: 'Inflation Option Base',
      fieldDescription: `This is the inflation protection percentage associated with the Base amount. If the policy does not have this option, then return 0%.`,
    }),
    fieldOverride(policyInflationProtectionOnCOBAmount, {
      fieldTitle: 'Inflation Option Rider',
      fieldDescription: `This is the inflation protection percentage associated with the Rider. If the policy does not have this option, then return 0%.`,
    }),
    policyInflationProtectionType,
  ],
  [FundingPolicyType.jointHybridLifeIra]: [
    hasCobRider,
    isPrimaryPolicyHolder,
    expectedPolicyActivationAge,
    policyTotalLTCBalance,
    {
      ...policyContinuationBenefitAmount,
      fieldDescription: '',
    },
    {
      ...monthlyBenefitWithdrawlRate,
      fieldDescription: '',
    },
    ...getMaxBenefitBenefitPerPeriodFields([MaxBenefitPeriod.daily, MaxBenefitPeriod.monthly]),
    ...hybridLifeIraCommonFields,
    ...hybridLifeIraFields,
    fieldOverride(policyInflationProtectionOnFaceAmount, {
      fieldTitle: 'Inflation Option Base',
      fieldDescription: `This is the inflation protection percentage associated with the Base amount. If the policy does not have this option, then return 0%.`,
    }),
    fieldOverride(policyInflationProtectionOnCOBAmount, {
      fieldTitle: 'Inflation Option Rider',
      fieldDescription: `This is the inflation protection percentage associated with the Rider. If the policy does not have this option, then return 0%.`,
    }),
    policyInflationProtectionType,
  ],
  [FundingPolicyType.nylSecureCare]: [
    policyMaximumBenefitAmount,
    ...getMaxBenefitBenefitPerPeriodFields([MaxBenefitPeriod.daily]),
    policyPremiumStartYear,
    policyPremiumMonthlyCost,
    fieldOverride(policyInflationProtectionPercentage, {
      fieldDescription: `If the stated inflation protection begins with "CPI-U" and is not a number like 3% or 5%, then return 0% inflation protection. Similarly, if there is no mention of inflation protection on the same table as the premium, then return 0%.`,
    }),
    fieldOverride(policyPremiumIncreaseRate, {
      fieldDescription: `This value is the same as the policy's inflation protection percentage. If there is no inflation protection, return 0%.`,
    }),
    fieldOverride(homeCareWaitingPeriodDays, {
      fieldDescription: `Always set this value to 20 days. For location, write that we assume the policyholder will use the care-coordination benefit which reduces the home waiting period to 20 days.`,
    }),
    fieldOverride(facilityCareWaitingPeriodDays, {
      fieldTitle: 'Facility Waiting Period',
      fieldDescription: `This value is labled as just "Waiting Period". It is most commonly 90 days. It is the period during which the policyholder must cover care costs out-of-pocket before insurance benefits begin. `,
    }),
  ],
  [FundingPolicyType.nylJointSecureCare]: [
    fieldOverride(policyMaximumBenefitAmount, {
      fieldDescription: `Look for the exact label "Policy Lifetime Maximum". Take this amount (for either Applicant 1 or 2) and multiply it by 3. In the location description, write "Individual lifetime maximum multiplied by 3, assuming shared care rider." We multiply by three because each partner has their own policy with the same maximum benefit amount and the shared care rider provides a third pool of equal value.`,
    }),
    ...getMaxBenefitBenefitPerPeriodFields([MaxBenefitPeriod.daily]),
    policyPremiumStartYear,
    fieldOverride(policyPremiumMonthlyCost, {
      fieldDescription: `Use the amount under the "Total" column that combines both applicants' "Total Premium" amounts. Do not use the "Total Premium" of just one applicant.`,
    }),
    fieldOverride(policyInflationProtectionPercentage, {
      fieldDescription: `If the stated inflation protection begins with "CPI-U" and is not a number like 3% or 5%, then return 0% inflation protection. Similarly, if there is no mention of inflation protection on the same table as the premium, then return 0%.`,
    }),
    fieldOverride(policyPremiumIncreaseRate, {
      fieldDescription: `This value is the same as the policy's inflation protection percentage. If there is no inflation protection, return 0%.`,
    }),
    fieldOverride(homeCareWaitingPeriodDays, {
      fieldDescription: `Always set this value to 20 days. For location, write that we assume the policyholder will use the care-coordination benefit which reduces the home waiting period to 20 days.`,
    }),
    fieldOverride(facilityCareWaitingPeriodDays, {
      fieldTitle: 'Facility Waiting Period',
      fieldDescription: `This value is labled as just "Waiting Period". It is most commonly 90 days. It is the period during which the policyholder must cover care costs out-of-pocket before insurance benefits begin. `,
    }),
  ],
  [FundingPolicyType.nylAssetFlex]: [
    fieldOverride(deathBenefitFaceAmount, {
      fieldTitle: 'LTC Acceleration Benefit',
      fieldDescription: `The LTC Acceleration Benefit is the amount available for reimbursement of expenses associated with Qualified LTC Services. It should be similar or equal to the intial face amount.`,
    }),
    fieldOverride(policyTotalLTCBenefitAmount, {
      fieldDescription: `The Total LTC Benefit Amount is the maximum amount that can be accessed to fund long-term care. This amount should equal the LTC Acceleration Benefit plus the Extension of Benefit amount.`,
    }),
    fieldOverride(policyContinuationBenefitAmount, {
      fieldTitle: `Extension of Benefits (EoB) Amount`,
      fieldDescription: `The Extension of Benefits Amount (EoB Amount), is a separate pool from the Face Amount which can be accessed to fund long-term care.`,
    }),
    fieldOverride(policyMinimumGuaranteedBenefitAmount, {
      fieldTitle: 'Residual Death Benefit',
      fieldDescription: `The residual death benefit is 10% of the "Initial Face Amount" (found under "POLICY COMPONENTS" on the first few pages).`,
    }),
    ...getMaxBenefitBenefitPerPeriodFields([MaxBenefitPeriod.daily, MaxBenefitPeriod.monthly]),
    fieldOverride(homeCareWaitingPeriodDays, {
      fieldDescription: `Assume a 0-day waiting period for home care since the policyholder will use the care-coordination benefit.`,
    }),
    fieldOverride(facilityCareWaitingPeriodDays, {
      fieldDescription: `This is typically always 90 days. Details can be found in the Narrative Summary.`,
    }),
    policyPremiumStartYear,
    fieldOverride(policyPremiumMonthlyCost, {
      fieldTitle: 'Annual Premium (converted to monthly)',
      fieldDescription: `Look for the text "Annual Planned Premium Amount". Divide this amount by 12 to get the monthly premium. If single pay, enter 0.`,
    }),
    fieldOverride(policyLimitedPayYears, {
      fieldTitle: 'X-Pay Annual Premium Payments',
      fieldDescription: `If premium is annual pay, look for the text "X-Pay Annual Planned Premium" and return the number associated to X (e.g 5 or 10). If single-pay, leave blank.`,
    }),
    fieldOverride(policyLumpSumPayment, {
      fieldTitle: 'Single-Pay Planned Premium',
      fieldDescription: `Look for the text "Single-Pay Planned Premium" under the section "PREMIUM AND OTHER INFORMATION". If premium is annual-pay, this field should be left blank.`,
    }),
    fieldOverride(policyInflationProtectionPercentage, {
      fieldTitle: 'Automatic Compounding Inflation Option',
      fieldDescription: `This value is the "Automatic Compounding Inflation Option" percentage found under Policy Components. If the policy does not have this option, then return 0.`,
    }),
  ],
  [FundingPolicyType.secureCareThree]: [
    fieldOverride(deathBenefitFaceAmount, {
      fieldTitle: 'Face Amount/Death Benefit',
      fieldDescription: `This data can be found in the table on page 2. There are two mentions of "Face Amount". The one we want is the higher of the two and the one first mentioned. The second face amount is related to the "Detailed premium report", which is NOT what we want. The correct "Face Amount" value is next to Elimination period and guaranteed minimum death benefit. This is the amount that the policy will pay out upon death, and which can be accessed early via qualifying long-term care costs.`,
    }),
    fieldOverride(policyMinimumGuaranteedBenefitAmount, {
      fieldDescription: `The minimum guaranteed death benefit that will pay out upon the policyholder's passing even if the death benefit was exhausted to pay for long-term care costs.`,
    }),
    policyTotalLTCBenefitAmount,
    fieldOverride(policyTotalLTCBenefitAmount, {
      fieldTitle: 'Day 1: LTC Benefits (Total)',
      fieldDescription: `This refers to the total amount of benefit that can be applied to pay for long-term care costs.`,
    }),
    fieldOverride(policyContinuationBenefitAmount, {
      fieldTitle: 'Extension of LTC, Day 1 Amount',
      fieldDescription: `This amount cannot be found in the table on page 2. However, it can be calculated by subtracting the Face Amount from the Day 1: LTC Benefits (Total).`,
    }),
    ...getMaxBenefitBenefitPerPeriodFields([MaxBenefitPeriod.daily, MaxBenefitPeriod.monthly]),
    ...commonFields,
    fieldOverride(policyInflationProtectionPercentage, {
      fieldTitle: 'LTC Inflation Protection Agreement',
    }),
    policyInflationProtectionType,
  ],
  [FundingPolicyType.assetCare]: [
    deathBenefitFaceAmount,
    fieldOverride(policyContinuationBenefitAmount, {
      fieldTitle: 'Continuation of Benefits',
      fieldDescription: `The Continuation of Benefits Amount (CoB Amount) is a separate pool from the Face Amount which can be accessed to fund long-term care. For this policy, look for the keyword "Unlimited". If present, return "unlimited" even if there is an actual amount listed. If not, return the actual amount.`,
    }),
    ...getMaxBenefitBenefitPerPeriodFields([MaxBenefitPeriod.daily, MaxBenefitPeriod.monthly]),
    fieldOverride(homeCareWaitingPeriodDays, {
      fieldTitle: 'Elimination Period',
      fieldDescription: `Look for the "Elimination Period" section under the "How does Asset Care work?" page. This is the period during which the policyholder must cover care costs out-of-pocket for Home Health Care before insurance benefits begin.`,
    }),
    fieldOverride(facilityCareWaitingPeriodDays, {
      fieldTitle: 'Elimination Period',
      fieldDescription: `Look for the "Elimination Period" section under the "How does Asset Care work?" page. This is the period during which the policyholder must cover care costs out-of-pocket for all services beside Home Health Care before insurance benefits begin.`,
    }),
    policyPremiumStartYear,
    policyPremiumMonthlyCost,
    policyLimitedPayYears,
    policyLumpSumPayment,
    fieldOverride(policyLumpSumPayment, {
      fieldTitle: 'Total initial premium',
    }),
    policyPaymentType,
    fieldOverride(policyPaymentType, {
      fieldTitle: 'Payment Type (Reimbursement or Indemnity)',
      fieldDescription: `This is a reimbursement product so return "reimbursement". In the location description, write that the policy states that the insured will be paid up to the monthly benefit limit, which implies a reimbursement model.`,
    }),
    fieldOverride(policyInflationProtectionOnFaceAmount, {
      fieldTitle: 'Inflation Option AOB',
      fieldDescription: `This is the inflation protection percentage associated with the AOB. If the policy does not have this option, then return 0%.`,
    }),
    fieldOverride(policyInflationProtectionOnCOBAmount, {
      fieldTitle: 'Inflation Option COB',
      fieldDescription: `This is the inflation protection percentage associated with the COB. If the policy does not have this option, then return 0%.`,
    }),
    fieldOverride(policyInflationProtectionYears, {
      fieldTitle: 'Inflation Duration',
      fieldDescription:
        'This is the number of years the policy benefit amounts will inflate. If this is Lifetime, then return "Lifetime". Otherwise, return the number of years.',
    }),
  ],
  [FundingPolicyType.careMattersTwo]: [
    fieldOverride(deathBenefitFaceAmount, {
      fieldTitle: 'Specified amount (amount accerlerated for long-term care)',
    }),
    fieldOverride(policyTotalLTCBenefitAmount, {
      fieldTitle: 'Total long-term care benefit',
      fieldDescription: `This is Day 1 total amount available for long-term care expenses. Make sure you extract the Day 1 amount. It is the sum of the specified amount and the continuation of benefits amount.`,
    }),
    fieldOverride(policyContinuationBenefitAmount, {
      fieldDescription: `This amount is not found within the table on page 2. However, it can be calculated by subtracting the specified amount from the total long-term care benefit.`,
    }),
    fieldOverride(policyMinimumGuaranteedBenefitAmount, {
      fieldDescription: `The minimum guaranteed death benefit that will pay out upon the policyholder's passing even if the death benefit was exhausted to pay for long-term care costs.`,
    }),
    ...getMaxBenefitBenefitPerPeriodFields([MaxBenefitPeriod.daily, MaxBenefitPeriod.monthly]),
    ...commonFields,
    fieldOverride(policyInflationProtectionPercentage, {
      fieldTitle: 'Inflation protection option',
      fieldDescription: `If the policy has inflation protection, return the percentage. If "declined", then return 0.`,
    }),
    policyInflationProtectionType,
  ],
  [FundingPolicyType.jointAssetCare]: [
    deathBenefitFaceAmount,
    fieldOverride(policyContinuationBenefitAmount, {
      fieldTitle: 'Continuation of Benefits',
      fieldDescription: `The Continuation of Benefits Amount (CoB Amount) is a separate pool from the Face Amount which can be accessed to fund long-term care. For this policy, look for the keyword "Unlimited". If present, return "unlimited" even if there is an actual amount listed. If not, return the actual amount.`,
    }),
    ...getMaxBenefitBenefitPerPeriodFields([MaxBenefitPeriod.daily, MaxBenefitPeriod.monthly]),
    fieldOverride(homeCareWaitingPeriodDays, {
      fieldTitle: 'Elimination Period',
      fieldDescription: `Look for the "Elimination Period" section under the "How does Asset Care work?" page. This is the period during which the policyholder must cover care costs out-of-pocket for Home Health Care before insurance benefits begin.`,
    }),
    fieldOverride(facilityCareWaitingPeriodDays, {
      fieldTitle: 'Elimination Period',
      fieldDescription: `Look for the "Elimination Period" section under the "How does Asset Care work?" page. This is the period during which the policyholder must cover care costs out-of-pocket for all services beside Home Health Care before insurance benefits begin.`,
    }),
    policyPremiumStartYear,
    policyPremiumMonthlyCost,
    policyLimitedPayYears,
    policyLumpSumPayment,
    fieldOverride(policyLumpSumPayment, {
      fieldTitle: 'Total initial premium',
    }),
    policyPaymentType,
    fieldOverride(policyPaymentType, {
      fieldTitle: 'Payment Type (Reimbursement or Indemnity)',
      fieldDescription: `This is a reimbursement product so return "reimbursement". In the location description, write that the policy states that the insured will be paid up to the monthly benefit limit, which implies a reimbursement model.`,
    }),
    fieldOverride(policyInflationProtectionOnFaceAmount, {
      fieldTitle: 'Inflation Option AOB',
      fieldDescription: `This is the inflation protection percentage associated with the AOB. If the policy does not have this option, then return 0%.`,
    }),
    fieldOverride(policyInflationProtectionOnCOBAmount, {
      fieldTitle: 'Inflation Option COB',
      fieldDescription: `This is the inflation protection percentage associated with the COB. If the policy does not have this option, then return 0%.`,
    }),
    fieldOverride(policyInflationProtectionYears, {
      fieldTitle: 'Inflation Duration',
      fieldDescription:
        'This is the number of years the policy benefit amounts will inflate. If this is Lifetime, then return "Lifetime". Otherwise, return the number of years.',
    }),
  ],
  [FundingPolicyType.jointCareMatters]: [
    fieldOverride(deathBenefitFaceAmount, {
      fieldTitle: 'Specified amount (amount accerlerated for long-term care)',
    }),
    fieldOverride(policyTotalLTCBenefitAmount, {
      fieldTitle: 'Total long-term care benefit',
      fieldDescription: `This is Day 1 total amount available for long-term care expenses. Make sure you extract the Day 1 amount. It is the sum of the specified amount and the continuation of benefits amount.`,
    }),
    fieldOverride(policyContinuationBenefitAmount, {
      fieldDescription: `This amount is not found within the table on page 3. However, it can be calculated by subtracting the specified amount from the total long-term care benefit.`,
    }),
    fieldOverride(policyMinimumGuaranteedBenefitAmount, {
      fieldDescription: `The minimum guaranteed death benefit that will pay out upon the policyholder's passing even if the death benefit was exhausted to pay for long-term care costs.`,
    }),
    ...getMaxBenefitBenefitPerPeriodFields([MaxBenefitPeriod.daily, MaxBenefitPeriod.monthly]),
    ...commonFields,
    fieldOverride(policyInflationProtectionPercentage, {
      fieldTitle: 'Inflation protection option',
      fieldDescription: `If the policy has inflation protection, return the percentage. If "declined", then return 0.`,
    }),
    policyInflationProtectionType,
  ],
  [FundingPolicyType.annuityCareTwo]: [
    fieldOverride(policyTotalLTCBalance, {
      fieldTitle: 'Total LTC Balance',
    }),
    ...getMaxBenefitBenefitPerPeriodFields([
      MaxBenefitPeriod.daily,
      MaxBenefitPeriod.monthly,
      MaxBenefitPeriod.annual,
    ]),
    homeCareWaitingPeriodDays,
    facilityCareWaitingPeriodDays,
    policyPremiumStartYear,
    policyPremiumMonthlyCost,
    policyLumpSumPayment,
    fieldOverride(policyPremiumMonthlyCost, {
      fieldDescription: `This policy does not have monthly premium, so return 0.`,
    }),
    fieldOverride(policyPaymentType, {
      fieldDescription: `This is a reimbursement product so return "reimbursement". In the location description, write that the policy states that the LTC Withrawals states that the insured will be paid for qualified LTC expenses, which implies a reimbursement model.`,
    }),
  ],
  [FundingPolicyType.forecareFixedAnnuity]: [
    fieldOverride(policyTotalLTCBalance, {
      fieldTitle: 'Total LTC Benefit',
    }),
    ...getMaxBenefitBenefitPerPeriodFields([MaxBenefitPeriod.daily, MaxBenefitPeriod.monthly]),
    homeCareWaitingPeriodDays,
    facilityCareWaitingPeriodDays,
    policyPremiumStartYear,
    fieldOverride(policyPremiumMonthlyCost, {
      fieldDescription: `This policy does not have monthly premium, so return 0.`,
    }),
    policyLumpSumPayment,
    fieldOverride(policyPaymentType, {
      fieldDescription: `This is a reimbursement product so return "reimbursement". In the location description, write that the "About ForeCare" section states that exceess coverage is only available for qualified LTC expenses, which implies a reimbursement model.`,
    }),
  ],
  [FundingPolicyType.jointForecareFixedAnnuity]: [
    fieldOverride(policyTotalLTCBalance, {
      fieldTitle: 'Total LTC Benefit',
    }),
    ...getMaxBenefitBenefitPerPeriodFields([MaxBenefitPeriod.daily, MaxBenefitPeriod.monthly]),
    homeCareWaitingPeriodDays,
    facilityCareWaitingPeriodDays,
    policyPremiumStartYear,
    fieldOverride(policyPremiumMonthlyCost, {
      fieldDescription: `This policy does not have monthly premium, so return 0.`,
    }),
    policyLumpSumPayment,
    fieldOverride(policyPaymentType, {
      fieldDescription: `This is a reimbursement product so return "reimbursement". In the location description, write that the "About ForeCare" section states that exceess coverage is only available for qualified LTC expenses, which implies a reimbursement model.`,
    }),
  ],
  [FundingPolicyType.indexedAnnuityCare]: [
    hasCobRider,
    fieldOverride(policyTotalLTCBalance, {
      fieldTitle: 'LTC Benefit Balance',
    }),
    fieldOverride(policyContinuationBenefitAmount, {
      fieldDescription: '',
    }),
    fieldOverride(monthlyBenefitWithdrawlRate, {
      fieldDescription:
        'To find the base withdrawal period, look for a line in the policy labeled “Base Benefit Period,” “Basic Withdrawal Period,” or similar, usually stated as a number of months (e.g., “24 months”). Once you find it, convert it into a percentage by dividing 1 by that number of months, then multiply by 100 (for example, 1/24 = 4.17%). This represents the monthly percentage of the LTC benefit balance available for withdrawal. If the base period isn’t stated, find a table showing both the LTC Benefit Balance and the LTC Monthly Withdrawal Limit and divide the latter by the former to infer the same percentage.',
    }),
    ...getMaxBenefitBenefitPerPeriodFields([MaxBenefitPeriod.daily, MaxBenefitPeriod.monthly]),
    fieldOverride(policyLumpSumPayment, {
      fieldDescription: `This value will depend on Has COB Rider Option. If "Has COB Rider Option" is yes, then look for the exact row label "1st Year Premium" associated to the column "Base Policy + COB Rider". This amount should be more than the 1st year premium for just the base policy. If "Has COB Rider Option" is no, then get the "1st Year Premium" associated to the column "Base Policy".
    This data should be located next to "Subsequent COB Premium" amount. Importantly, DO NOT return the "Contract Premium" amount which is found in the header of several pages.`,
    }),
    fieldOverride(policyLimitedPayYears, {
      fieldDescription: `Look for the number of payments needed for the COB rider. It will be in the format "COB X-Pay Rider"`,
    }),
    fieldOverride(policyPremiumMonthlyCost, {
      fieldDescription: `Look for the "Subsequent COB Premium" which should be annual. If so, divide this amount by 12 to get the monthly premium amount.`,
    }),
    fieldOverride(policyInflationProtectionOnFaceAmount, {
      fieldTitle: 'Inflation Option Base',
      fieldDescription: `This is the inflation protection percentage associated with the Base amount. If the policy does not have this option, then return 0%.`,
    }),
    fieldOverride(policyInflationProtectionOnCOBAmount, {
      fieldTitle: 'Inflation Option Rider',
      fieldDescription: `This is the inflation protection percentage associated with the COB Rider. If the policy does not have this option, then return 0%.`,
    }),
    fieldOverride(policyInflationProtectionType, {
      fieldTitle: 'Inflation Type (Rider)',
      fieldDescription: `Inflation protection can be either compound or simple. This should be specified next to the inflation protection percentage. If the policy does not specify, enter null.`,
    }),
    homeCareWaitingPeriodDays,
    facilityCareWaitingPeriodDays,
    policyPremiumStartYear,
  ],
  [FundingPolicyType.assetcareThree]: [
    hasCobRider,
    fieldOverride(policyTotalLTCBalance, {
      fieldTitle: 'Death Benefit/LTC Benefit Balance',
    }),
    fieldOverride(policyContinuationBenefitAmount, {
      fieldDescription: '',
    }),
    fieldOverride(monthlyBenefitWithdrawlRate, {
      fieldDescription: '',
    }),
    ...getMaxBenefitBenefitPerPeriodFields([MaxBenefitPeriod.daily, MaxBenefitPeriod.monthly]),
    fieldOverride(policyLumpSumPayment, {
      fieldDescription: `This value will depend on Has COB Rider Option. If "Has COB Rider Option" is yes, then look for the exact row label "1st Year Premium" associated to the column "Base Policy + COB Rider". This amount should be more than the 1st year premium for just the base policy. If "Has COB Rider Option" is no, then get the "1st Year Premium" associated to the column "Base Policy".
    This data should be located next to "Subsequent COB Premium" amount. Importantly, DO NOT return the "Contract Premium" amount which is found in the header of several pages.`,
    }),
    fieldOverride(policyLimitedPayYears, {
      fieldDescription: `Look for the number of payments needed for the COB rider. It will be in the format "COB X-Pay Rider"`,
    }),
    fieldOverride(policyPremiumMonthlyCost, {
      fieldDescription: `Look for the "Subsequent COB Premium" which should be annual. If so, divide this amount by 12 to get the monthly premium amount.`,
    }),
    fieldOverride(policyInflationProtectionOnFaceAmount, {
      fieldTitle: 'Inflation Option Base',
      fieldDescription: `This is the inflation protection percentage associated with the Base amount. If the policy does not have this option, then return 0%.`,
    }),
    fieldOverride(policyInflationProtectionOnCOBAmount, {
      fieldTitle: 'Inflation Option Rider',
      fieldDescription: `This is the inflation protection percentage associated with the COB Rider. If the policy does not have this option, then return 0%.`,
    }),
    fieldOverride(policyInflationProtectionType, {
      fieldTitle: 'Inflation Type (Rider)',
      fieldDescription: `Inflation protection can be either compound or simple. This should be specified next to the inflation protection percentage. If the policy does not specify, enter null.`,
    }),
    homeCareWaitingPeriodDays,
    facilityCareWaitingPeriodDays,
    policyPremiumStartYear,
  ],
  [FundingPolicyType.jointAssetcareThree]: [
    hasCobRider,
    fieldOverride(policyTotalLTCBalance, {
      fieldTitle: 'Total Death Benefit/LTC Benefit Balance',
    }),
    fieldOverride(policyContinuationBenefitAmount, {
      fieldDescription: '',
    }),
    fieldOverride(monthlyBenefitWithdrawlRate, {
      fieldDescription: '',
    }),
    ...getMaxBenefitBenefitPerPeriodFields([MaxBenefitPeriod.daily, MaxBenefitPeriod.monthly]),
    fieldOverride(policyLumpSumPayment, {
      fieldDescription: `This value will depend on Has COB Rider Option. If "Has COB Rider Option" is yes, then look for the exact row label "1st Year Premium" associated to the column "Base Policy + COB Rider". This amount should be more than the 1st year premium for just the base policy. If "Has COB Rider Option" is no, then get the "1st Year Premium" associated to the column "Base Policy".
    This data should be located next to "Subsequent COB Premium" amount. Importantly, DO NOT return the "Contract Premium" amount which is found in the header of several pages.`,
    }),
    fieldOverride(policyLimitedPayYears, {
      fieldDescription: `Look for the number of payments needed for the COB rider. It will be in the format "COB X-Pay Rider"`,
    }),
    fieldOverride(policyPremiumMonthlyCost, {
      fieldDescription: `Look for the "Subsequent COB Premium" which should be annual. If so, divide this amount by 12 to get the monthly premium amount.`,
    }),
    fieldOverride(policyInflationProtectionOnFaceAmount, {
      fieldTitle: 'Inflation Option Base',
      fieldDescription: `This is the inflation protection percentage associated with the Base amount. If the policy does not have this option, then return 0%.`,
    }),
    fieldOverride(policyInflationProtectionOnCOBAmount, {
      fieldTitle: 'Inflation Option Rider',
      fieldDescription: `This is the inflation protection percentage associated with the COB Rider. If the policy does not have this option, then return 0%.`,
    }),
    fieldOverride(policyInflationProtectionType, {
      fieldTitle: 'Inflation Type (Rider)',
      fieldDescription: `Inflation protection can be either compound or simple. This should be specified next to the inflation protection percentage. If the policy does not specify, enter null.`,
    }),
    homeCareWaitingPeriodDays,
    facilityCareWaitingPeriodDays,
    policyPremiumStartYear,
  ],
  [FundingPolicyType.noLapseGuaranteeLife]: [
    fieldOverride(deathBenefitFaceAmount, {
      fieldTitle: 'Long-Term Care Rider Specified Amount',
    }),
    fieldOverride(policyLumpSumPayment, {
      fieldTitle: 'Initial Planned Premium',
    }),
    fieldOverride(policyMinimumGuaranteedBenefitAmount, {
      fieldTitle: 'Residual Death Benefit',
      fieldDescription: `The policy states in the Residual Death Benefit Endorsement that the residual death benefit is 10% of the initial specified amount.`,
    }),
    ...getMaxBenefitBenefitPerPeriodFields([MaxBenefitPeriod.daily, MaxBenefitPeriod.monthly]),
    homeCareWaitingPeriodDays,
    facilityCareWaitingPeriodDays,
    policyPremiumStartYear,
    policyPremiumMonthlyCost,
    policyLimitedPayYears,
    fieldOverride(policyInflationProtectionPercentage, {
      fieldDescription:
        'This policy does not have inflation protection, so return 0. In the location description, write that the policy does not mention inflation protection.',
    }),
    fieldOverride(policyPaymentType, {
      fieldDescription: `This is an indemnity policy return "indemnity". In the location description, write that the policy mentions cash indemnity beneits in the Narrative Summary.`,
    }),
  ],
  [FundingPolicyType.jointThriventLongTermCareInsurance]: [
    fieldOverride(policyMaximumBenefitAmount, {
      fieldTitle: 'Available Benefit Amount',
      fieldDescription: `This is the maximum amount available to spend on long-term care for both policyholders combined. Therefore, you need to sum the available benefit amounts for both policyholders. The amounts for each policyholder should be equal to each other.`,
    }),
    ...getMaxBenefitBenefitPerPeriodFields([MaxBenefitPeriod.daily, MaxBenefitPeriod.monthly]),
    homeCareWaitingPeriodDays,
    facilityCareWaitingPeriodDays,
    policyPremiumStartYear,
    fieldOverride(policyPremiumMonthlyCost, {
      fieldDescription: `This is the monthly premium for both policyholders combined. Therefore, you need to sum the monthly premiums for both policyholders. The amounts for each policyholder are likely different. If the modal premium is annual, then divide it by 12 to get the monthly premium.`,
    }),
    policyLimitedPayYears,
    policyLumpSumPayment,
    policyPaymentType,
    policyInflationProtectionPercentage,
    policyInflationProtectionType,
  ],
  [FundingPolicyType.jointMutualOfOmahaMutualcare]: [
    fieldOverride(policyMaximumBenefitAmount, {
      fieldTitle: 'Policy Limit',
      fieldDescription: `This is the maximum amount available to spend on long-term care for both policyholders combined. Therefore, you need to sum the policy limits for both policyholders. The amounts for each policyholder should be equal to each other.`,
    }),
    ...getMaxBenefitBenefitPerPeriodFields([MaxBenefitPeriod.daily, MaxBenefitPeriod.monthly]),
    homeCareWaitingPeriodDays,
    facilityCareWaitingPeriodDays,
    policyPremiumStartYear,
    fieldOverride(policyPremiumMonthlyCost, {
      fieldDescription: `This is the monthly premium for both policyholders combined. Therefore, take the combined premium under the Total premium section. If this premium amount is annual ("Your annual premium"), then divide by 12 to get the combined monthly premium.`,
    }),
    policyLimitedPayYears,
    policyLumpSumPayment,
    policyPaymentType,
    policyInflationProtectionPercentage,
    policyInflationProtectionType,
  ],
};

const policyTypeFieldMapTyped = policyTypeFieldMap as Partial<
  Record<FundingPolicyType, PolicyPromptField<any>[]>
>;

export const getPolicyPromptFields = (policyType: FundingPolicyType): PolicyPromptField<any>[] => {
  // If the policy type exists in policyTypeFormFieldMap, return its fields
  if (policyTypeFieldMapTyped[policyType]) {
    return policyTypeFieldMapTyped[policyType]!;
  }

  // Find the policy definition in fundingPolicyTypeDefList
  const policyDef = fundingPolicyTypeDefList.find(def => def.value === policyType);

  // If the policy type is missing, check for its genericType and return those fields
  if (policyDef?.genericType && policyTypeFieldMapTyped[policyDef.genericType]) {
    return policyTypeFieldMapTyped[policyDef.genericType]!;
  }

  // If the policy type is missing, check for its calculationType and return those fields
  if (policyDef && policyTypeFieldMapTyped[policyDef.calculationType]) {
    return policyTypeFieldMapTyped[policyDef.calculationType]!;
  }

  // If neither is found, throw error
  throw new Error(`Invalid policy type, ${policyType}`);
};
